import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const scrollTo = (href) => scrollToId(href);

// Desktop primary items (kept short to avoid crowding)
const PRIMARY = [
  { label: "Homes for Sale", href: "#properties" },
  { label: "About", href: "#about" },
  { label: "Client Reviews", href: "#reviews" },
];

// Grouped under the "Info & Tools" dropdown
const INFO_TOOLS = [
  { label: "Market Report", href: "#newsletter", testid: "nav-link-market-report" },
  { label: "Mortgage Calculator", to: "/calculators", testid: "nav-link-calculators" },
  { label: "Neighbourhood Buzzer", to: "/neighbourhood-buzzer", testid: "nav-link-buzzer" },
  { label: "Home Evaluation", to: "/home-evaluation", testid: "nav-link-home-evaluation" },
  { label: "Recently Sold", href: "#sold", testid: "nav-link-recently-sold" },
  { label: "Buying Tips", to: "/buying-tips", testid: "nav-link-buying-tips" },
  { label: "Selling Tips", to: "/selling-tips", testid: "nav-link-selling-tips" },
];

// Grouped under the "Community" dropdown
const COMMUNITY = [
  { label: "The Neighbourhood", to: "/community/neighbourhood", testid: "nav-link-neighbourhood" },
  { label: "Local Businesses", to: "/community/local-businesses", testid: "nav-link-local-businesses" },
  { label: "Community Events", to: "/community/events", testid: "nav-link-community-events" },
];

// Mobile shows one simple flat list of everything
const MOBILE_LINKS = [
  { label: "Waterfront Homes for Sale", href: "#properties" },
  { label: "Lagoon City Questions Answered", to: "/buying-in-lagoon-city" },
  { label: "Recently Sold", href: "#sold" },
  { label: "Home Evaluation", to: "/home-evaluation" },
  { label: "Mortgage Calculator", to: "/calculators" },
  { label: "Neighbourhood Buzzer", to: "/neighbourhood-buzzer" },
  { label: "Market Report", href: "#newsletter" },
  { label: "Buying Tips", to: "/buying-tips" },
  { label: "Selling Tips", to: "/selling-tips" },
  { label: "About", href: "#about" },
  { label: "Client Reviews", href: "#reviews" },
  { label: "The Neighbourhood", to: "/community/neighbourhood" },
  { label: "Local Businesses", to: "/community/local-businesses" },
  { label: "Community Events", to: "/community/events" },
];

const testId = (label) => `nav-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

function NavDropdown({ label, items, testid }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const openMenu = () => {
    clearTimeout(timer.current);
    setOpen(true);
  };
  const closeMenu = () => {
    timer.current = setTimeout(() => setOpen(false), 120);
  };
  return (
    <div className="relative" onMouseEnter={openMenu} onMouseLeave={closeMenu}>
      <button
        data-testid={testid}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-sm text-slate-300 hover:text-[#F2A7BA] transition-colors duration-300 tracking-wide whitespace-nowrap"
      >
        {label}
        <ChevronDown size={14} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3">
          <div className="min-w-[210px] rounded-2xl border border-[#E890A7]/15 bg-[#151218]/95 backdrop-blur-xl shadow-2xl shadow-black/50 p-2">
            {items.map((g) =>
              g.external ? (
                <a
                  key={g.testid}
                  data-testid={g.testid}
                  href={g.external}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setOpen(false)}
                  className="block rounded-xl px-4 py-2.5 text-sm text-slate-300 hover:text-[#F2A7BA] hover:bg-[#E890A7]/8 transition-colors whitespace-nowrap"
                >
                  {g.label}
                </a>
              ) : g.to ? (
                <Link
                  key={g.testid}
                  data-testid={g.testid}
                  to={g.to}
                  onClick={() => setOpen(false)}
                  className="block rounded-xl px-4 py-2.5 text-sm text-slate-300 hover:text-[#F2A7BA] hover:bg-[#E890A7]/8 transition-colors whitespace-nowrap"
                >
                  {g.label}
                </Link>
              ) : (
                <button
                  key={g.testid}
                  data-testid={g.testid}
                  onClick={() => {
                    setOpen(false);
                    scrollTo(g.href);
                  }}
                  className="block w-full text-left rounded-xl px-4 py-2.5 text-sm text-slate-300 hover:text-[#F2A7BA] hover:bg-[#E890A7]/8 transition-colors whitespace-nowrap"
                >
                  {g.label}
                </button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#0D0B0E]/85 backdrop-blur-xl border-b border-[#E890A7]/10 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <button
          data-testid="nav-logo-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <span className="bg-white/95 rounded-full px-3 py-1.5 shadow-lg shadow-black/40 group-hover:scale-105 transition-transform duration-300">
            <img src="/assets/joselle-logo.png" alt="Joselle Alves Broker" className="h-6 w-auto" />
          </span>
          <span className="hidden sm:block font-mono text-[10px] tracking-[0.3em] uppercase text-[#E890A7]/80">
            Lagoon City
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-6">
          <button
            data-testid="nav-link-homes-for-sale"
            onClick={() => scrollTo("#properties")}
            className="text-sm text-slate-300 hover:text-[#F2A7BA] transition-colors duration-300 tracking-wide whitespace-nowrap"
          >
            Waterfront Homes for Sale
          </button>

          <Link
            data-testid="nav-link-questions-answered"
            to="/buying-in-lagoon-city"
            className="text-sm text-slate-300 hover:text-[#F2A7BA] transition-colors duration-300 tracking-wide whitespace-nowrap"
          >
            Lagoon City Questions Answered
          </Link>

          {/* Info & Tools dropdown */}
          <NavDropdown label="Info & Tools" items={INFO_TOOLS} testid="nav-link-info-tools-btn" />

          {PRIMARY.filter((l) => l.href !== "#properties").map((l) => (
            <button
              key={l.href}
              data-testid={testId(l.label)}
              onClick={() => scrollTo(l.href)}
              className="text-sm text-slate-300 hover:text-[#F2A7BA] transition-colors duration-300 tracking-wide whitespace-nowrap"
            >
              {l.label}
            </button>
          ))}

          <NavDropdown label="Community" items={COMMUNITY} testid="nav-link-community-btn" />
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <Link
            data-testid="nav-owner-login-link"
            to="/admin"
            className="text-xs font-mono tracking-widest uppercase text-slate-500 hover:text-[#E890A7] transition-colors"
          >
            Owner Login
          </Link>
          <button
            data-testid="nav-consultation-btn"
            onClick={() => scrollTo("#contact")}
            className="rounded-full bg-[#E890A7] text-[#0D0B0E] text-sm font-semibold px-5 py-2.5 hover:bg-[#F2A7BA] hover:-translate-y-0.5 transition-all duration-300"
          >
            Private Consultation
          </button>
        </div>

        <button
          data-testid="nav-mobile-menu-btn"
          className="lg:hidden text-slate-200"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden bg-[#151218]/95 backdrop-blur-xl border-t border-[#E890A7]/10 mt-3">
          <div className="px-6 py-6 flex flex-col gap-5">
            {MOBILE_LINKS.map((l) =>
              l.external ? (
                <a
                  key={l.label}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  href={l.external}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setOpen(false)}
                  className="text-left text-lg font-display text-slate-200 hover:text-[#F2A7BA] transition-colors"
                >
                  {l.label}
                </a>
              ) : l.to ? (
                <Link
                  key={l.label}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="text-left text-lg font-display text-slate-200 hover:text-[#F2A7BA] transition-colors"
                >
                  {l.label}
                </Link>
              ) : (
                <button
                  key={l.label}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => {
                    setOpen(false);
                    scrollTo(l.href);
                  }}
                  className="text-left text-lg font-display text-slate-200 hover:text-[#F2A7BA] transition-colors"
                >
                  {l.label}
                </button>
              )
            )}
            <Link data-testid="nav-mobile-owner-login" to="/admin" className="text-xs font-mono tracking-widest uppercase text-slate-500">
              Owner Login
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
